import { type Dayjs } from 'dayjs'
import React from 'react'

import { Tag } from '@fourthwall/components'
import { useShopTime } from '@modules/Timezone/providers/ShopTimeProvider'
import { getDaysLeft, getHoursLeft, isBeforeNow } from '@utils/date'
import { isTimeFrameFrom, isTimeFrameNone, isTimeFrameRange, isTimeFrameTo } from '@utils/timeframe'

import { STATUSES } from './consts'
import type { StatusTagProps } from './types'

export const StatusTag = ({
  status,
  timeFrame,
  size = 'medium',
  icon,
  hoverable = false,
  isSoldOut = false,
}: StatusTagProps) => {
  const { toShopTime } = useShopTime()

  const getFormattedDate = (date: string | undefined) => {
    if (!date) return

    return toShopTime(date)
  }

  const formattedFrom =
    timeFrame && (isTimeFrameFrom(timeFrame) || isTimeFrameRange(timeFrame))
      ? getFormattedDate(timeFrame.from)
      : undefined

  const formattedTo =
    timeFrame && (isTimeFrameTo(timeFrame) || isTimeFrameRange(timeFrame))
      ? getFormattedDate(timeFrame.to)
      : undefined

  const getStartDate = (date: Dayjs) => date.format('MMM D')

  // eslint-disable-next-line react/no-unstable-nested-components
  const StartingTag = ({ startDate }: { startDate: string }) => {
    return <Tag label={`Going public ${startDate}`} appearance="alert" icon={icon} size={size} />
  }

  // eslint-disable-next-line react/no-unstable-nested-components
  const EndingTag = ({ endDate }: { endDate: Dayjs }) => {
    const daysLeft = getDaysLeft(endDate)
    const hoursLeft = getHoursLeft(endDate)

    const hoursLabel = hoursLeft > 0 ? `${hoursLeft} hours left` : 'less than an hour left'
    const label = daysLeft > 0 ? `${daysLeft} days left` : hoursLabel

    return <Tag label={`Public - ${label}`} appearance="success" icon={icon} size={size} />
  }

  // eslint-disable-next-line react/no-unstable-nested-components
  const EndedTag = () => {
    return <Tag label="Ended" appearance="default" icon={icon} size={size} />
  }

  // eslint-disable-next-line react/no-unstable-nested-components
  const PublicTag = () => {
    return <Tag label="Public" appearance="success" icon={icon} size={size} />
  }

  if (timeFrame) {
    if (isTimeFrameNone(timeFrame)) {
      if (status === 'HIDDEN') {
        return <Tag label="Hidden" appearance="alert" icon={icon} size={size} />
      }

      return <PublicTag />
    }

    if (isTimeFrameTo(timeFrame) && formattedTo) {
      if (isBeforeNow(formattedTo)) {
        return <EndedTag />
      }

      return <EndingTag endDate={formattedTo} />
    }

    if (isTimeFrameFrom(timeFrame) && formattedFrom) {
      if (isBeforeNow(formattedFrom)) {
        return <PublicTag />
      }

      return <StartingTag startDate={getStartDate(formattedFrom)} />
    }

    if (isTimeFrameRange(timeFrame) && formattedTo && formattedFrom) {
      if (isBeforeNow(formattedTo)) {
        return <EndedTag />
      }

      if (!isBeforeNow(formattedFrom)) {
        return <StartingTag startDate={getStartDate(formattedFrom)} />
      }

      return <EndingTag endDate={formattedTo} />
    }
  }

  return (
    <Tag
      label={`${STATUSES[status]?.label || status}${isSoldOut ? ' - sold out' : ''}`}
      appearance={STATUSES[status]?.appearance}
      size={size}
      icon={icon}
      hoverable={hoverable}
    />
  )
}
